import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

// A curtain of the aurora: a soft spine across part of the sky, rays hanging up from it. Each one
// covers only part of the width and leans, so the curtains overlap and never lie as parallel bands
// (owner rule: no stripes of colour).
interface Ribbon {
  x0: number;
  x1: number;
  /** The spine's height at x0 and x1 (fractions of the board), so the curtain leans. */
  y0: number;
  y1: number;
  /** The rays' reach above the spine, a fraction of the board's height. */
  reach: number;
  /** Which sprite it wears: 0 = green edge, blue body; 1 = blue edge, lavender body; 2 = lavender edge, pink body. */
  tone: number;
  strength: number;
  phase: number[];
}

interface Star {
  x: number;
  y: number;
  r: number;
  a: number;
  twinkle: number;
}

// The board is soft light, so it is drawn at half the page's pixels and the browser scales it up:
// a quarter of the fill each frame, and the blur comes for free.
const RES = 0.5;
// The sky's motion is one seamless loop (owner, 2026-10-16: "tam loop olsun"): every wave turns a whole
// number of times in LOOP seconds of sky time, so the sky comes back to where it started and never jumps
const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;
// the column pitch of a curtain, in page pixels: each ray is drawn four columns wide, so they melt
const PITCH = 6;

/**
 * Aurora Borealis' hero board: curtains of the northern lights over a faint starfield, swaying and
 * rippling slowly. The pointer over the stage (or a finger, or the counter counting) wakes them: the
 * glow brightens, the rays reach higher and ripple faster, most of all round the pointer. The colours
 * are the theme's (--theme-color-secondary at the curtain's edge, --theme-color in its body,
 * --theme-color-tertiary at the top), read live. The logo, motto and counter keep a soft clearing so
 * they stay readable. Reduced motion (and the page's wallpaper, `frozen`) draws one still moment.
 */
@Component( {
  selector: 'app-hero-aurora',
  template: '<canvas #canvas class="ha-canvas" aria-hidden="true"></canvas>',
  styleUrls: [ './hero-aurora.component.scss' ],
} )
export class HeroAuroraComponent extends HeroMotifBase {

  private ribbons: Ribbon[] = [];
  private stars: Star[] = [];
  private sprites: HTMLCanvasElement[] = [];

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.res = RES;
    this.loop = LOOP;
  }

  protected colorsChanged(): void {
    this.sprites = [];
  }

  /**
   * Lay the curtains and the stars. A wide board gets four curtains leaning across it, each over part
   * of the width; a tall one (a phone) gets them stacked down the sky, wider and shallower.
   */
  protected build( rand: () => number ): void {
    const tall = this.height > this.width * 1.1;
    const j = ( spread: number ) => ( rand() * 2 - 1 ) * spread;
    const layout: [ number, number, number, number, number, number, number ][] = tall
      // x0, x1, y0, y1, reach, tone, strength
      ? [
        [ -0.1, 0.8, 0.30, 0.22, 0.20, 0, 1 ],
        [ 0.15, 1.1, 0.52, 0.44, 0.22, 1, 0.9 ],
        [ -0.1, 0.75, 0.78, 0.70, 0.18, 2, 0.75 ],
      ]
      : [
        [ -0.08, 0.62, 0.46, 0.32, 0.34, 0, 1 ],
        [ 0.34, 1.08, 0.66, 0.50, 0.38, 0, 0.95 ],
        [ -0.06, 0.46, 0.86, 0.74, 0.26, 1, 0.7 ],
        [ 0.58, 1.06, 0.30, 0.20, 0.22, 2, 0.6 ],
      ];
    this.ribbons = layout.map( ( [ x0, x1, y0, y1, reach, tone, strength ] ) => ( {
      x0: x0 + j( 0.04 ), x1: x1 + j( 0.04 ), y0: y0 + j( 0.03 ), y1: y1 + j( 0.03 ),
      reach: reach * ( 0.9 + rand() * 0.2 ), tone, strength,
      phase: [ 0, 1, 2, 3, 4, 5 ].map( () => rand() * Math.PI * 2 ),
    } ) );
    const count = Math.round( this.width * this.height / 7000 );
    this.stars = [];
    for ( let i = 0; i < count; i++ ) {
      this.stars.push( {
        x: rand(), y: rand(),
        r: 0.6 + rand() * rand() * 1.6, a: 0.25 + rand() * 0.55, twinkle: rand() * Math.PI * 2,
      } );
    }
  }

  protected paint( ctx: CanvasRenderingContext2D ): void {
    if ( !this.sprites.length ) { this.makeSprites(); }
    const e = this.energy;
    const t = this.time;

    // the night: the palette's dark colour tints the top of the sky, fading down to the page
    if ( this.paletteColors.length >= 5 && !this.lightSurface ) {
      const night = ctx.createLinearGradient( 0, 0, 0, this.height );
      night.addColorStop( 0, this.rgba( this.paletteColors[ 0 ], 0.55 ) );
      night.addColorStop( 0.6, this.rgba( this.paletteColors[ 0 ], 0 ) );
      ctx.fillStyle = night;
      ctx.fillRect( 0, 0, this.width, this.height );
    }

    // the stars, faint, a few of them twinkling
    const starInk = this.lightSurface ? this.colors[ 0 ] : [ 235, 242, 255 ] as RGB;
    for ( const s of this.stars ) {
      const tw = 0.65 + 0.35 * Math.sin( t * W0 * 15 + s.twinkle * 7 );
      ctx.fillStyle = this.rgba( starInk, s.a * tw * ( this.lightSurface ? 0.35 : 0.8 ) );
      ctx.beginPath();
      ctx.arc( s.x * this.width, s.y * this.height, s.r, 0, Math.PI * 2 );
      ctx.fill();
    }

    // the curtains: one ray per column, its sprite stretched from above the spine to just under it.
    // Light adds up on a dark sky; on a light one it is laid on, softer.
    ctx.globalCompositeOperation = this.lightSurface ? 'source-over' : 'lighter';
    const H = this.height;
    const W = this.width;
    const rest = this.lightSurface ? 0.28 : 0.5;
    const awake = this.lightSurface ? 0.25 : 0.55;
    for ( const r of this.ribbons ) {
      const sprite = this.sprites[ r.tone ];
      const xa = r.x0 * W;
      const xb = r.x1 * W;
      const p = r.phase;
      for ( let x = Math.max( -PITCH * 2, xa ); x <= Math.min( W + PITCH * 2, xb ); x += PITCH ) {
        const u = ( x - xa ) / ( xb - xa );
        // the curtain fades in and out at its ends, never a cut edge
        const env = Math.pow( Math.sin( Math.PI * Math.min( 1, Math.max( 0, u ) ) ), 0.9 );
        if ( env < 0.02 ) { continue; }
        // the spine: a lean across, a long slow swell and a shorter wave on it
        const y = H * ( r.y0 + ( r.y1 - r.y0 ) * u )
          + H * 0.055 * Math.sin( x * 0.0042 + t * W0 * 3 + p[ 0 ] )
          + H * 0.022 * Math.sin( x * 0.013 - t * W0 * 6 + p[ 1 ] )
          + H * 0.008 * Math.sin( x * 0.037 + t * W0 * 10 + p[ 2 ] );
        // the folds: where the curtain turns toward us it glows, where it turns away it dims
        const fold = 0.5 + 0.5 * Math.sin( x * 0.009 + t * W0 * 5 + p[ 3 ] ) * Math.cos( x * 0.0031 - t * W0 * 3 + p[ 4 ] );
        // the rays: a faint shimmer travelling along the curtain, two waves so it never repeats evenly;
        // kept low, or the rays line up as vertical stripes (owner rule: no bands of colour)
        const rays = 0.74 + 0.15 * Math.sin( x * 0.11 + t * W0 * 12 + p[ 5 ] ) + 0.11 * Math.sin( x * 0.041 - t * W0 * 8 );
        const reach = H * r.reach * ( 0.55 + 0.45 * fold ) * ( 1 + 0.3 * e );
        // the pointer's own glow: the curtain round it brightens and reaches up
        let boost = 0;
        if ( this.near > 0.01 ) {
          const dx = x - this.pointerX;
          const dy = ( y - reach * 0.35 ) - this.pointerY;
          boost = this.near * Math.exp( -( dx * dx + dy * dy * 0.6 ) / ( 2 * 170 * 170 ) );
        }
        const alpha = Math.min( 1, r.strength * env * ( 0.2 + 0.8 * fold ) * rays
          * ( rest + awake * e ) * ( 1 + 1.4 * boost * Math.max( e, 0.4 ) ) );
        if ( alpha < 0.01 ) { continue; }
        const top = reach * ( 1 + 0.35 * boost );
        ctx.globalAlpha = alpha;
        // the sprite's edge sits at 86% of its height: the rays above, a little glow under the edge
        ctx.drawImage( sprite, x - PITCH * 2, y - top, PITCH * 4, top / 0.86 );
      }
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';

    // the logo, the motto and the counter keep a soft clearing, so the words read over the sky
    if ( this.blocks.length && !this.frozen ) {
      ctx.globalCompositeOperation = 'destination-out';
      for ( const b of this.blocks ) {
        const cx = b.x + b.w / 2;
        const cy = b.y + b.h / 2;
        const rx = b.w / 2 + 60;
        const ry = b.h / 2 + 40;
        ctx.save();
        ctx.translate( cx, cy );
        ctx.scale( rx, ry );
        const g = ctx.createRadialGradient( 0, 0, 0, 0, 0, 1 );
        g.addColorStop( 0, 'rgba(0,0,0,0.55)' );
        g.addColorStop( 0.6, 'rgba(0,0,0,0.4)' );
        g.addColorStop( 1, 'rgba(0,0,0,0)' );
        ctx.fillStyle = g;
        ctx.fillRect( -1, -1, 2, 2 );
        ctx.restore();
      }
      ctx.globalCompositeOperation = 'source-over';
    }
  }

  /**
   * The rays, painted once per colour change: a tall narrow sprite, its sharp bright edge near the
   * bottom (the aurora's lower edge), its colour climbing from the edge colour through the body to
   * the top colour as it fades out, and soft at its sides so neighbouring rays melt together.
   */
  private makeSprites(): void {
    const [ primary, secondary, tertiary ] = this.colors;
    // edge, body, crown of each curtain: the colour circle's four lights when the theme gives them
    // (Aurora: green, blue, pink, lavender), the theme's three accents otherwise
    const lights = this.paletteColors.length >= 5 ? this.paletteColors.slice( 1, 5 ) : null;
    const tones: [ RGB, RGB, RGB ][] = lights
      ? [
        [ lights[ 1 ], lights[ 0 ], lights[ 2 ] ],
        [ lights[ 0 ], lights[ 3 ], lights[ 2 ] ],
        [ lights[ 3 ], lights[ 2 ], lights[ 2 ] ],
      ]
      : [
        [ secondary, primary, tertiary ],
        [ primary, tertiary, tertiary ],
        [ tertiary, primary, primary ],
      ];
    this.sprites = tones.map( ( [ edge, body, crown ] ) => {
      const c = document.createElement( 'canvas' );
      c.width = 16;
      c.height = 256;
      const g = c.getContext( '2d' )!;
      const v = g.createLinearGradient( 0, 0, 0, 256 );
      v.addColorStop( 0, this.rgba( crown, 0 ) );
      v.addColorStop( 0.24, this.rgba( crown, 0.16 ) );
      v.addColorStop( 0.52, this.rgba( body, 0.36 ) );
      v.addColorStop( 0.74, this.rgba( edge, 0.72 ) );
      v.addColorStop( 0.86, this.rgba( this.lightSurface ? edge : this.mix( edge, [ 255, 255, 255 ], 0.35 ), 1 ) );
      v.addColorStop( 0.9, this.rgba( edge, 0.45 ) );
      v.addColorStop( 1, this.rgba( edge, 0 ) );
      g.fillStyle = v;
      g.fillRect( 0, 0, 16, 256 );
      g.globalCompositeOperation = 'destination-in';
      const h = g.createLinearGradient( 0, 0, 16, 0 );
      h.addColorStop( 0, 'rgba(0,0,0,0)' );
      h.addColorStop( 0.5, 'rgba(0,0,0,1)' );
      h.addColorStop( 1, 'rgba(0,0,0,0)' );
      g.fillStyle = h;
      g.fillRect( 0, 0, 16, 256 );
      return c;
    } );
  }
}
